import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Camera, ImagePlus } from "lucide-react";
import { COUNTRIES } from "./constants";
import AddLogoImage from "../../Images/addLogoImageLiteAndGrowth.png";

/* ---------------------------------- Flags --------------------------------- */

export const Flag = ({ code, className = "h-4 w-6" }) => {
  switch (code) {
    case "IN":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="India">
          <rect width="24" height="5.33" y="0" fill="#FF9933" rx="1" />
          <rect width="24" height="5.33" y="5.33" fill="#FFFFFF" />
          <rect width="24" height="5.34" y="10.66" fill="#138808" rx="1" />
          <circle cx="12" cy="8" r="1.6" fill="none" stroke="#000080" strokeWidth="0.7" />
        </svg>
      );
    case "US":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="USA">
          <rect width="24" height="16" fill="#B22234" rx="1" />
          {[2.4, 4.8, 7.2, 9.6, 12, 14.4].map((y) => (
            <rect key={y} width="24" height="1.2" y={y - 1.2} fill="#FFFFFF" />
          ))}
          <rect width="10" height="8.5" fill="#3C3B6E" rx="1" />
        </svg>
      );
    case "GB":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="UK">
          <rect width="24" height="16" fill="#012169" rx="1" />
          <path d="M0 0 L24 16 M24 0 L0 16" stroke="#FFFFFF" strokeWidth="3" />
          <path d="M0 0 L24 16 M24 0 L0 16" stroke="#C8102E" strokeWidth="1.2" />
          <rect x="10" width="4" height="16" fill="#FFFFFF" />
          <rect y="6" width="24" height="4" fill="#FFFFFF" />
          <rect x="11" width="2" height="16" fill="#C8102E" />
          <rect y="7" width="24" height="2" fill="#C8102E" />
        </svg>
      );
    case "AE":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="UAE">
          <rect width="24" height="16" fill="#00732F" rx="1" />
          <rect width="24" height="5.33" y="5.33" fill="#FFFFFF" />
          <rect width="24" height="5.34" y="10.66" fill="#000000" />
          <rect width="7" height="16" fill="#FF0000" rx="1" />
        </svg>
      );
    case "SG":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="Singapore">
          <rect width="24" height="8" fill="#EF3340" rx="1" />
          <rect width="24" height="8" y="8" fill="#FFFFFF" rx="1" />
          <circle cx="7" cy="4" r="2.4" fill="#FFFFFF" />
          <circle cx="8" cy="4" r="2" fill="#EF3340" />
        </svg>
      );
    case "AU":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="Australia">
          <rect width="24" height="16" fill="#00247D" rx="1" />
          <circle cx="6" cy="5" r="1" fill="#FFF" />
          <circle cx="14" cy="3" r="0.8" fill="#FFF" />
          <circle cx="18" cy="7" r="0.8" fill="#FFF" />
          <circle cx="15" cy="12" r="0.8" fill="#FFF" />
          <circle cx="9" cy="11" r="1.2" fill="#FFF" />
        </svg>
      );
    case "DE":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="Germany">
          <rect width="24" height="5.33" fill="#000000" rx="1" />
          <rect width="24" height="5.33" y="5.33" fill="#DD0000" />
          <rect width="24" height="5.34" y="10.66" fill="#FFCC00" rx="1" />
        </svg>
      );
    case "FR":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="France">
          <rect width="8" height="16" fill="#002654" rx="1" />
          <rect width="8" height="16" x="8" fill="#FFFFFF" />
          <rect width="8" height="16" x="16" fill="#ED2939" rx="1" />
        </svg>
      );
    case "JP":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="Japan">
          <rect width="24" height="16" fill="#FFFFFF" rx="1" />
          <circle cx="12" cy="8" r="4" fill="#BC002D" />
        </svg>
      );
    case "CA":
      return (
        <svg viewBox="0 0 24 16" className={className} aria-label="Canada">
          <rect width="6" height="16" fill="#FF0000" rx="1" />
          <rect width="12" height="16" x="6" fill="#FFFFFF" />
          <rect width="6" height="16" x="18" fill="#FF0000" rx="1" />
          <path d="M12 4 L13 7 L14.5 6.5 L13.5 9 L15 9.5 L12.5 10 L12.5 12 L11.5 12 L11.5 10 L9 9.5 L10.5 9 L9.5 6.5 L11 7 Z" fill="#FF0000" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 16" className={className}>
          <rect width="24" height="16" fill="#E5E7EB" rx="1" />
        </svg>
      );
  }
};

/* ------------------------------- Field label ------------------------------ */
/* Figma: Inter / Medium 500 / 14px / lh 20px / color TEXT/PRIMARY #1F2020     */

export const FieldLabel = ({ children, required }) => (
  <label className="block text-[14px] font-medium leading-5 text-[#1F2020] mb-1.5">
    {children}
    {required && <span className="text-[#AD0101] ml-0.5">*</span>}
  </label>
);

/* ------------------------------- Text field ------------------------------- */

export const TextField = ({
  label,
  required,
  placeholder,
  value,
  onChange,
  error,
  disabled,
  name,
  maxLength,
  inputMode,
}) => (
  <div>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <input
      type="text"
      name={name}
      value={value}
      disabled={disabled}
      maxLength={maxLength}
      inputMode={inputMode}
      placeholder={placeholder}
      onChange={(e) => onChange && onChange(e.target.value)}
      className="w-full h-[42px] rounded-md border border-[#D9DDE3] px-3 text-[14px] leading-5 text-[#1F2020] placeholder:text-[#737373] bg-white
        focus:outline-none focus:border-[#ADB4BE] transition
        disabled:bg-[#F3F4F6] disabled:text-gray-500 disabled:cursor-not-allowed"
    />
    {error && <p className="mt-1 ml-1 text-[11px] leading-4 text-[#AD0101]">{error}</p>}
  </div>
);

/* ------------------------------ Custom dropdown ---------------------------- */

export const CustomSelect = ({
  label,
  required,
  placeholder = "Select",
  value,
  onChange,
  options = [],
  error,
  disabled,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-[42px] rounded-md border border-[#D9DDE3] px-3 flex items-center justify-between text-[14px] bg-white
          focus:outline-none focus:border-[#ADB4BE] transition
          disabled:bg-[#F3F4F6] disabled:text-gray-500 disabled:cursor-not-allowed
          ${value ? "text-[#1F2020]" : "text-[#737373]"}`}
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {options.length === 0 && (
            <div className="px-3 py-2 text-[13px] text-gray-400">No options available</div>
          )}
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange && onChange(opt);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#FDECEA] hover:text-[#E11D2E] transition
                ${opt === value ? "bg-[#FDECEA] text-[#E11D2E] font-medium" : "text-gray-700"}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-1 ml-1 text-[11px] leading-4 text-[#AD0101]">{error}</p>}
    </div>
  );
};

/* ---------------------------- Country code select -------------------------- */

export const CountryCodeSelect = ({ value = "IN", onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = COUNTRIES.find((c) => c.code === value) || COUNTRIES[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="h-[42px] px-2 flex items-center gap-1.5 bg-white rounded-l-md border-r border-[#D9DDE3] disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"
      >
        <Flag code={selected.code} />
        <span className="text-[13px] text-gray-700">{selected.dialCode}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {COUNTRIES.map((c) => (
            <button
              key={c.code + c.dialCode}
              type="button"
              onClick={() => {
                onChange && onChange(c.code);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-[#FDECEA] transition ${
                c.code === value ? "bg-[#FDECEA] text-[#E11D2E] font-medium" : "text-gray-700"
              }`}
            >
              <Flag code={c.code} />
              <span className="flex-1 text-left truncate">{c.name}</span>
              <span className="text-gray-400">{c.dialCode}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* --------------------------------- Phone field ----------------------------- */

export const PhoneField = ({
  label,
  required,
  placeholder = "Enter Mobile Number",
  value,
  countryCode,
  onChange,
  onCountryChange,
  error,
  disabled,
}) => (
  <div>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <div
      className={`flex rounded-md border border-[#D9DDE3] bg-white overflow-hidden
        focus-within:border-[#ADB4BE] transition
        ${disabled ? "bg-[#F3F4F6]" : ""}`}
    >
      <CountryCodeSelect value={countryCode} onChange={onCountryChange} disabled={disabled} />
      <input
        type="text"
        inputMode="numeric"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={10}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
          onChange && onChange(digits);
        }}
        className="flex-1 h-[42px] px-3 text-[14px] leading-5 text-[#1F2020] placeholder:text-[#737373] focus:outline-none
          disabled:bg-[#F3F4F6] disabled:text-gray-500 disabled:cursor-not-allowed"
      />
    </div>
    {error && <p className="mt-1 ml-1 text-[11px] leading-4 text-[#AD0101]">{error}</p>}
  </div>
);

/* --------------------------------- Logo upload ----------------------------- */

export const LogoUpload = ({ preview, onFileSelect, disabled }) => {
  const inputRef = useRef(null);

  const handlePick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && onFileSelect) onFileSelect(file);
    e.target.value = "";
  };

  return (
    <div className="relative inline-block w-[156px] h-[156px]">
      <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleChange} />
      <button
        type="button"
        onClick={handlePick}
        disabled={disabled}
        className="w-[156px] h-[156px] rounded-full overflow-hidden flex items-center justify-center transition bg-[#F7F8FA]
          disabled:cursor-not-allowed disabled:opacity-60"
      >
        {preview ? (
          <img src={preview} alt="Company logo" className="h-full w-full object-cover" />
        ) : (
          <img
            src={AddLogoImage}
            alt="Add your logo"
            className="h-full w-full object-cover"
          />
        )}
      </button>
      <button
        type="button"
        onClick={handlePick}
        disabled={disabled}
        className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-white border border-gray-300 shadow flex items-center justify-center hover:border-[#AD0101] transition"
      >
        <Camera className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  );
};

/* --------------------------------- Radio option ---------------------------- */

export const RadioOption = ({ checked, onChange, label, disabled, name }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange && onChange()}
    className="flex items-center gap-3 group disabled:cursor-not-allowed"
  >
    <span className="text-[18.6px] font-normal text-[#393939]">{label}</span>
    <span
      className={`h-[18px] w-[18px] rounded-full border flex items-center justify-center transition bg-white
        ${checked ? "border-[#AD0101]" : "border-gray-400 group-hover:border-[#AD0101]"}`}
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="h-[12px] w-[12px]">
          <path
            d="M2.5 6.2 L4.8 8.4 L9.5 3.6"
            fill="none"
            stroke="#AD0101"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  </button>
);

/* --------------------------------- Section card ---------------------------- */
/* Figma: radius 12px / padding 24px 32px / shadow 0 0 4px rgba(0,0,0,0.15)    */

export const SectionCard = ({ number, title, right, children }) => (
  <div
    className="bg-white rounded-[12px] px-8 py-6 mb-5"
    style={{ boxShadow: "0px 0px 4px rgba(0,0,0,0.15)" }}
  >
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-[20px] leading-6 font-semibold text-[#1F2020]">
        {number}. {title}
      </h3>
      {right}
    </div>
    {children}
  </div>
);
